import { doc, getDoc, setDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { db } from './config';
import { storageService } from './storageService';
import { dogService } from './dogService';
import { DogDocument } from '@/types/dog';

/**
 * 犬の血統書管理サービスクラス
 */
export class DocumentService {
  /**
   * 血統書メタデータの作成・更新
   * @description 1個体につき1つの血統書のみを管理（上書き）
   */
  async createOrUpdateDocument(
    dogId: string,
    fileName: string,
    fileUrl: string,
    fileType: 'pdf' | 'image',
    userId: string
  ): Promise<DogDocument> {
    try {
      // 既存の血統書を削除（上書きのため）
      await this.deleteDocumentIfExists(dogId, userId);

      // 固定IDを使用（1個体につき1つのみ）
      const documentId = 'pedigree';
      const documentRef = doc(db, 'dogs', dogId, 'documents', documentId);

      const document: DogDocument = {
        id: documentId,
        dogId,
        fileName,
        fileUrl,
        fileType,
        uploadedAt: Timestamp.now(),
      };

      await setDoc(documentRef, document);

      // DogのpedigreeDocumentUrlを更新
      await this.updateDogDocument(dogId, fileUrl, userId);

      return document;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統書メタデータの作成に失敗しました: ${error.message}`);
      }
      throw new Error('血統書メタデータの作成に失敗しました');
    }
  }

  /**
   * 血統書取得
   */
  async getDocument(dogId: string, userId: string): Promise<DogDocument | null> {
    try {
      // 権限チェック: 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const documentId = 'pedigree';
      const documentRef = doc(db, 'dogs', dogId, 'documents', documentId);
      const documentSnap = await getDoc(documentRef);

      if (!documentSnap.exists()) {
        return null;
      }

      return documentSnap.data() as DogDocument;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統書の取得に失敗しました: ${error.message}`);
      }
      throw new Error('血統書の取得に失敗しました');
    }
  }

  /**
   * 血統書削除（FirestoreとStorage両方）
   */
  async deleteDocument(dogId: string, userId: string): Promise<void> {
    try {
      // 権限チェック: 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      const documentId = 'pedigree';
      const documentRef = doc(db, 'dogs', dogId, 'documents', documentId);
      const documentSnap = await getDoc(documentRef);

      if (!documentSnap.exists()) {
        throw new Error('血統書が見つかりません');
      }

      const document = documentSnap.data() as DogDocument;

      // Storageから削除
      await storageService.deleteFile(document.fileUrl);

      // Firestoreから削除
      await deleteDoc(documentRef);

      // DogのpedigreeDocumentUrlをクリア
      await this.updateDogDocument(dogId, undefined, userId);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統書の削除に失敗しました: ${error.message}`);
      }
      throw new Error('血統書の削除に失敗しました');
    }
  }

  /**
   * 既存の血統書を削除（内部用）
   */
  private async deleteDocumentIfExists(dogId: string, userId: string): Promise<void> {
    try {
      const existingDocument = await this.getDocument(dogId, userId);
      if (existingDocument) {
        await this.deleteDocument(dogId, userId);
      }
    } catch (error) {
      // 削除エラーは無視（既に存在しない場合など）
      console.warn('既存血統書の削除中にエラー:', error);
    }
  }

  /**
   * 血統書アップロードとメタデータ保存を一度に実行
   */
  async uploadDocumentWithMetadata(
    file: File,
    dogId: string,
    userId: string
  ): Promise<DogDocument> {
    try {
      // 個体の所有者確認
      const dog = await dogService.getDog(dogId, userId);
      if (!dog) {
        throw new Error('個体が見つかりません');
      }

      // Storageにアップロード
      const fileUrl = await storageService.uploadDocument(file, dogId, userId);

      // ファイルタイプを判定
      const fileType: 'pdf' | 'image' = file.type === 'application/pdf' ? 'pdf' : 'image';

      // メタデータを保存（既存があれば上書き）
      const document = await this.createOrUpdateDocument(
        dogId,
        file.name,
        fileUrl,
        fileType,
        userId
      );

      return document;
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(`血統書アップロードに失敗しました: ${error.message}`);
      }
      throw new Error('血統書アップロードに失敗しました');
    }
  }

  /**
   * DogのpedigreeDocumentUrlを更新
   */
  private async updateDogDocument(
    dogId: string,
    pedigreeDocumentUrl: string | undefined,
    userId: string
  ): Promise<void> {
    await dogService.updateDog(dogId, { pedigreeDocumentUrl } as any, userId);
  }
}

// シングルトンインスタンスをエクスポート
export const documentService = new DocumentService();
